<script setup lang="ts">
import MarkdownIt from 'markdown-it'
import texmath from 'markdown-it-texmath'
import markdownItAbbr from 'markdown-it-abbr'
import markdownItDeflist from 'markdown-it-deflist'
import markdownItFootnote from 'markdown-it-footnote'
import markdownItMark from 'markdown-it-mark'
import { full as markdownItEmoji } from 'markdown-it-emoji'
import markdownItSub from 'markdown-it-sub'
import markdownItSup from 'markdown-it-sup'
import katex from 'katex'
import hljs from 'highlight.js'
import 'highlight.js/styles/github.min.css'
import 'katex/dist/katex.min.css'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { formatDisplayDate } from '~/composables/useDisplayText'
import { bindProgressiveImages } from '~/composables/useProgressiveImages'
import { collectContentTocItems, slugifyContentHeading } from '~/utils/contentToc'
import { getStandalonePostTarget } from '~/utils/embeddedPostCards'
import type { EmbeddedPostCardMap } from '~/utils/embeddedPostCards'

type MarkdownLeadingLink = {
  text: string
  href: string
}

const props = withDefaults(defineProps<{
  source?: string
  leadingLink?: MarkdownLeadingLink
  decodeMarkdownMarkers?: boolean
  tocEnabled?: boolean
  postCards?: EmbeddedPostCardMap
}>(), {
  tocEnabled: true
})
const emit = defineEmits<{
  (event: 'toc-change', value: Array<{ id: string; text: string; level: number }>): void
}>()
const markdownRootRef = ref<HTMLElement | null>(null)
const lineNumbersReady = ref(false)
const colorMode = useColorMode()
const {
  visible: markdownLightboxVisible,
  sources: markdownLightboxSources,
  index: markdownLightboxIndex,
  open: openMarkdownLightboxImages,
  close: closeMarkdownLightbox
} = useImageLightbox()

const copyLabel = '复制'
const copiedLabel = '已复制'
const copyFailedLabel = '复制失败'
const mermaidRenderErrorText = '图表渲染失败'

const encodeCopyPayload = (value: string) => {
  const text = value || ''
  if (!text) {
    return ''
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(text, 'utf8').toString('base64')
  }
  if (typeof TextEncoder !== 'undefined' && typeof btoa !== 'undefined') {
    const bytes = new TextEncoder().encode(text)
    let binary = ''
    const chunkSize = 0x8000
    for (let index = 0; index < bytes.length; index += chunkSize) {
      const chunk = bytes.subarray(index, Math.min(index + chunkSize, bytes.length))
      binary += String.fromCharCode(...chunk)
    }
    return btoa(binary)
  }
  return text
}

const decodeCopyPayload = (value: string) => {
  const text = value || ''
  if (!text) {
    return ''
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(text, 'base64').toString('utf8')
  }
  if (typeof TextDecoder !== 'undefined' && typeof atob !== 'undefined') {
    const binary = atob(text)
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  }
  return text
}

type ServerSanitizeFn = (dirty: string, options: Record<string, unknown>) => string
let serverSanitizeHtml: ServerSanitizeFn | null = null

if (import.meta.server) {
  const mod = await import('sanitize-html')
  serverSanitizeHtml = mod.default as ServerSanitizeFn
}

const langLabel = (lang: string) => {
  const value = (lang || '').trim()
  return value ? value.toUpperCase() : 'TEXT'
}

const markdownTaskListPlugin = (md: MarkdownIt) => {
  md.core.ruler.after('inline', 'task-list', state => {
    const { tokens } = state
    for (let index = 2; index < tokens.length; index++) {
      const inlineToken = tokens[index]
      if (inlineToken.type !== 'inline') {
        continue
      }
      if (tokens[index - 1]?.type !== 'paragraph_open' || tokens[index - 2]?.type !== 'list_item_open') {
        continue
      }
      const match = inlineToken.content.match(/^\s*\[( |x|X)\]\s+/)
      if (!match) {
        continue
      }
      const checked = /x/i.test(match[1] || '')
      tokens[index - 2].attrJoin('class', 'task-list-item')
      for (let i = index - 3; i >= 0; i--) {
        const token = tokens[i]
        if (token.type === 'bullet_list_open' || token.type === 'ordered_list_open') {
          token.attrJoin('class', 'task-list')
          break
        }
      }
      inlineToken.content = inlineToken.content.slice(match[0].length)
      if (inlineToken.children) {
        for (const child of inlineToken.children) {
          if (child.type === 'text') {
            child.content = child.content.replace(/^\s*\[( |x|X)\]\s+/, '')
            break
          }
        }
        const checkbox = new state.Token('html_inline', '', 0)
        checkbox.content = `<input class="task-list-checkbox" type="checkbox" disabled${checked ? ' checked' : ''}>`
        inlineToken.children.unshift(checkbox)
      }
    }
  })
}

const CALLOUTS = {
  NOTE: 'Note',
  TIP: 'Tip',
  IMPORTANT: 'Important',
  WARNING: 'Warning',
  CAUTION: 'Caution'
} as const

type CalloutType = keyof typeof CALLOUTS

const markdownCalloutPlugin = (markdown: MarkdownIt) => {
  markdown.core.ruler.after('inline', 'callout', state => {
    const { tokens } = state
    for (let index = 0; index < tokens.length; index++) {
      if (
        tokens[index]?.type !== 'blockquote_open' ||
        tokens[index + 1]?.type !== 'paragraph_open' ||
        tokens[index + 2]?.type !== 'inline' ||
        tokens[index + 3]?.type !== 'paragraph_close'
      ) {
        continue
      }

      const [calloutHeader, ...leadingContentLines] = tokens[index + 2].content.split(/\r?\n/)
      const match = calloutHeader.match(/^\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\](?:[ \t]+(.*?))?\s*$/i)
      if (!match) {
        continue
      }

      const subtype = match[1].toUpperCase() as CalloutType
      const callout = CALLOUTS[subtype]
      let blockquoteDepth = 0
      let closeIndex = -1
      for (let tokenIndex = index; tokenIndex < tokens.length; tokenIndex++) {
        if (tokens[tokenIndex].type === 'blockquote_open') {
          blockquoteDepth++
        } else if (tokens[tokenIndex].type === 'blockquote_close' && --blockquoteDepth === 0) {
          closeIndex = tokenIndex
          break
        }
      }
      if (!callout || closeIndex < 0) {
        continue
      }

      const openToken = tokens[index]
      openToken.type = 'html_block'
      openToken.tag = ''
      openToken.nesting = 0
      openToken.content = `<div class="callout" data-subtype="${subtype}">\n`

      const headerToken = new state.Token('callout_header', '', 0)
      headerToken.content = match[2]?.trim() || callout
      const contentOpenToken = new state.Token('html_block', '', 0)
      contentOpenToken.content = '<div class="callout-content">\n'
      const leadingContent = leadingContentLines.join('\n').trim()
      const contentTokens = [headerToken, contentOpenToken]
      if (leadingContent) {
        const leadingContentToken = new state.Token('callout_leading_content', '', 0)
        leadingContentToken.content = leadingContent
        contentTokens.push(leadingContentToken)
      }
      const contentCloseToken = new state.Token('html_block', '', 0)
      contentCloseToken.content = '</div>\n'

      tokens.splice(index + 1, 3, ...contentTokens)
      const adjustedCloseIndex = closeIndex + contentTokens.length - 3
      tokens.splice(adjustedCloseIndex, 0, contentCloseToken)

      const closeToken = tokens[adjustedCloseIndex + 1]
      closeToken.type = 'html_block'
      closeToken.tag = ''
      closeToken.nesting = 0
      closeToken.content = '</div>\n'
    }
  })

  markdown.renderer.rules.callout_header = (tokens, index, _options, env) => {
    return `<div class="callout-info"><span class="callout-title">${markdown.renderInline(tokens[index].content, env)}</span></div>\n`
  }

  markdown.renderer.rules.callout_leading_content = (tokens, index, _options, env) => {
    return `<p>${markdown.renderInline(tokens[index].content, env)}</p>\n`
  }
}

const defaultAllowedTags = [
  'address', 'article', 'aside', 'footer', 'header', 'h1', 'h2', 'h3', 'h4',
  'h5', 'h6', 'hgroup', 'main', 'nav', 'section', 'blockquote', 'dd', 'div',
  'dl', 'dt', 'figcaption', 'figure', 'hr', 'li', 'ol', 'p', 'pre', 'ul',
  'a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'cite', 'code', 'data', 'dfn',
  'em', 'i', 'kbd', 'mark', 'q', 'rb', 'rp', 'rt', 'rtc', 'ruby', 's', 'samp',
  'small', 'span', 'strong', 'sub', 'sup', 'time', 'u', 'var', 'wbr', 'caption',
  'col', 'colgroup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr',
  'details', 'summary', 'del', 'ins', 'picture', 'video', 'audio', 'source',
  'track', 'iframe', 'form', 'label', 'fieldset', 'legend', 'textarea', 'select',
  'option', 'optgroup', 'datalist', 'progress', 'meter'
]

const defaultSelfClosing = ['img', 'br', 'hr', 'area', 'base', 'basefont', 'input', 'link', 'meta']

const allowedStyleRules: Record<string, RegExp[]> = {
  'font-size': [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  'margin-right': [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  'margin-left': [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  'margin-top': [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  'margin-bottom': [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  'padding-left': [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  'padding-right': [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  top: [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  right: [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  bottom: [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  left: [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  width: [/^(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  'min-width': [/^(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  'max-width': [/^(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  height: [/^(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/],
  'vertical-align': [/^-?(?:\d+|\d*\.\d+)(?:em|ex|px|rem|%)$/, /^0$/]
}

const markdownHtmlSanitizeOptions = {
  allowedTags: Array.from(
    new Set([
      ...defaultAllowedTags,
      'img',
      'button',
      'span',
      'input',
      'section',
      'article',
      'kbd',
      'sup',
      'sub',
      'math',
      'semantics',
      'annotation',
      'annotation-xml',
      'mrow',
      'mi',
      'mn',
      'mo',
      'ms',
      'mtext',
      'msup',
      'msub',
      'msubsup',
      'mfrac',
      'msqrt',
      'mroot',
      'mtable',
      'mtr',
      'mtd',
      'mspace',
      'munderover',
      'mover',
      'munder'
    ])
  ),
  allowedAttributes: {
    '*': ['class', 'id', 'data-subtype', 'aria-hidden'],
    a: ['href', 'name', 'target', 'rel', 'title'],
    img: ['src', 'srcset', 'alt', 'title', 'loading', 'decoding', 'fetchpriority', 'width', 'height'],
    button: ['type', 'class', 'title', 'aria-label', 'name', 'value', 'disabled', 'data-code'],
    span: ['class', 'style', 'aria-hidden'],
    input: ['type', 'checked', 'disabled', 'class', 'name', 'value', 'placeholder', 'readonly', 'required', 'min', 'max', 'step', 'minlength', 'maxlength', 'pattern'],
    code: ['class'],
    abbr: ['title'],
    blockquote: ['cite'],
    q: ['cite'],
    ol: ['start', 'reversed', 'type'],
    li: ['value'],
    time: ['datetime'],
    del: ['cite', 'datetime'],
    ins: ['cite', 'datetime'],
    details: ['open'],
    iframe: ['src', 'title', 'width', 'height', 'allow', 'allowfullscreen', 'loading', 'referrerpolicy', 'sandbox'],
    video: ['src', 'poster', 'controls', 'autoplay', 'loop', 'muted', 'playsinline', 'preload', 'width', 'height'],
    audio: ['src', 'controls', 'autoplay', 'loop', 'muted', 'preload'],
    source: ['src', 'srcset', 'type', 'media', 'sizes'],
    track: ['default', 'kind', 'label', 'src', 'srclang'],
    form: ['action', 'method', 'target', 'autocomplete', 'name'],
    label: ['for'],
    fieldset: ['disabled', 'name'],
    textarea: ['name', 'rows', 'cols', 'placeholder', 'readonly', 'required', 'minlength', 'maxlength'],
    select: ['name', 'multiple', 'required', 'size'],
    option: ['value', 'label', 'selected', 'disabled'],
    optgroup: ['label', 'disabled'],
    progress: ['value', 'max'],
    meter: ['value', 'min', 'max', 'low', 'high', 'optimum'],
    math: ['xmlns', 'display'],
    annotation: ['encoding'],
    th: ['colspan', 'rowspan', 'align'],
    td: ['colspan', 'rowspan', 'align']
  },
  allowedStyles: {
    span: allowedStyleRules
  },
  selfClosing: Array.from(new Set([...defaultSelfClosing, 'input'])),
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  allowedSchemesByTag: {
    img: ['http', 'https', 'data'],
    source: ['http', 'https', 'data'],
    video: ['http', 'https', 'data'],
    audio: ['http', 'https', 'data'],
    iframe: ['http', 'https']
  },
  allowedSchemesAppliedToAttributes: ['href', 'src', 'cite'],
  allowProtocolRelative: false,
  disallowedTagsMode: 'discard'
}

const markdownAllowedTags = new Set(markdownHtmlSanitizeOptions.allowedTags)
const markdownGlobalAttrs = new Set(markdownHtmlSanitizeOptions.allowedAttributes['*'] || [])
const markdownAllowedAttrsByTag = Object.fromEntries(
  Object.entries(markdownHtmlSanitizeOptions.allowedAttributes).map(([tag, attrs]) => [tag, new Set(attrs)])
) as Record<string, Set<string>>
const markdownSchemeAttrs = new Set(markdownHtmlSanitizeOptions.allowedSchemesAppliedToAttributes)
const markdownNonTextTags = new Set(['style', 'script', 'textarea', 'option'])

const sanitizeStyleText = (styleText: string) => {
  const segments = String(styleText || '').split(';')
  const result: string[] = []
  for (const segment of segments) {
    const pairIndex = segment.indexOf(':')
    if (pairIndex <= 0) {
      continue
    }
    const rawProp = segment.slice(0, pairIndex).trim().toLowerCase()
    const rawValue = segment.slice(pairIndex + 1).trim()
    const rules = allowedStyleRules[rawProp]
    if (!rules || !rawValue) {
      continue
    }
    if (rules.some(re => re.test(rawValue))) {
      result.push(`${rawProp}: ${rawValue}`)
    }
  }
  return result.join('; ')
}

const hasDisallowedScheme = (tagName: string, attrName: string, rawValue: string) => {
  if (!markdownSchemeAttrs.has(attrName)) {
    return false
  }
  const value = String(rawValue || '').trim()
  if (!value) {
    return false
  }
  if (value.startsWith('#')) {
    return false
  }
  if (value.startsWith('/')) {
    if (value.startsWith('//')) {
      return !markdownHtmlSanitizeOptions.allowProtocolRelative
    }
    return false
  }
  const match = value.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/)
  if (!match) {
    return false
  }
  const scheme = match[1].toLowerCase()
  const byTag = markdownHtmlSanitizeOptions.allowedSchemesByTag[tagName]
  const allowed = byTag || markdownHtmlSanitizeOptions.allowedSchemes
  return !allowed.includes(scheme)
}

const sanitizeHtmlInBrowser = (dirty: string) => {
  if (!import.meta.client) {
    return dirty
  }
  const template = document.createElement('template')
  template.innerHTML = String(dirty || '')

  const cleanNode = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return
    }
    const el = node as HTMLElement
    const tagName = el.tagName.toLowerCase()

    if (!markdownAllowedTags.has(tagName)) {
      if (markdownNonTextTags.has(tagName)) {
        el.remove()
        return
      }
      const childNodes = [...el.childNodes]
      childNodes.forEach(cleanNode)
      const parent = el.parentNode
      if (!parent) {
        return
      }
      childNodes.forEach(child => {
        parent.insertBefore(child, el)
      })
      parent.removeChild(el)
      return
    }

    const allowedAttrs = new Set(markdownGlobalAttrs)
    const tagAttrs = markdownAllowedAttrsByTag[tagName]
    if (tagAttrs) {
      tagAttrs.forEach(attr => allowedAttrs.add(attr))
    }

    for (const attr of [...el.attributes]) {
      const attrName = attr.name.toLowerCase()
      const attrValue = attr.value

      if (attrName.startsWith('on')) {
        el.removeAttribute(attr.name)
        continue
      }
      if (!allowedAttrs.has(attrName)) {
        el.removeAttribute(attr.name)
        continue
      }
      if (attrName === 'style') {
        const safeStyle = sanitizeStyleText(attrValue)
        if (!safeStyle) {
          el.removeAttribute('style')
        } else {
          el.setAttribute('style', safeStyle)
        }
        continue
      }
      if (hasDisallowedScheme(tagName, attrName, attrValue)) {
        el.removeAttribute(attr.name)
      }
    }

    for (const child of [...el.childNodes]) {
      cleanNode(child)
    }
  }

  for (const node of [...template.content.childNodes]) {
    cleanNode(node)
  }

  return template.innerHTML
}

const sanitizeMarkdownHtml = (dirty: string) => {
  if (serverSanitizeHtml) {
    return serverSanitizeHtml(dirty, markdownHtmlSanitizeOptions)
  }
  return sanitizeHtmlInBrowser(dirty)
}

const decodeMarkdownBlockMarkers = (source: string) =>
  source
    .split('\n')
    .map(line =>
      line.replace(/^([ \t]{0,3})(?:(?:&gt;|&#62;|&#x3e;)([ \t]?))+/i, match =>
        match.replace(/&gt;|&#62;|&#x3e;/gi, '>')
      )
    )
    .join('\n')

const markdownLeadingLinkPlugin = (md: MarkdownIt, leadingLink: MarkdownLeadingLink) => {
  md.core.ruler.after('inline', 'leading-link', state => {
    const text = String(leadingLink.text || '').trim()
    const href = String(leadingLink.href || '').trim()
    if (!text || !href) {
      return
    }

    const createLeadingLinkTokens = () => {
      const linkOpenToken = new state.Token('link_open', 'a', 1)
      linkOpenToken.attrs = [
        ['href', href],
        ['class', 'md-leading-link']
      ]
      linkOpenToken.level = 0
      const linkTextToken = new state.Token('text', '', 0)
      linkTextToken.content = text
      linkTextToken.level = 1
      const linkCloseToken = new state.Token('link_close', 'a', -1)
      linkCloseToken.level = 0
      return [linkOpenToken, linkTextToken, linkCloseToken]
    }

    const inlineIndex = state.tokens.findIndex((token, index) => {
      const paragraphOpen = state.tokens[index - 1]
      const paragraphClose = state.tokens[index + 1]
      return (
        token.type === 'inline' &&
        paragraphOpen?.type === 'paragraph_open' &&
        paragraphOpen.level === 0 &&
        !paragraphOpen.hidden &&
        paragraphClose?.type === 'paragraph_close' &&
        !paragraphClose.hidden
      )
    })
    if (inlineIndex < 0) {
      const paragraphOpenToken = new state.Token('paragraph_open', 'p', 1)
      paragraphOpenToken.block = true
      paragraphOpenToken.level = 0
      const inlineToken = new state.Token('inline', '', 0)
      inlineToken.block = true
      inlineToken.level = 1
      inlineToken.content = text
      inlineToken.children = createLeadingLinkTokens()
      const paragraphCloseToken = new state.Token('paragraph_close', 'p', -1)
      paragraphCloseToken.block = true
      paragraphCloseToken.level = 0
      state.tokens.unshift(paragraphOpenToken, inlineToken, paragraphCloseToken)
      return
    }

    const inlineToken = state.tokens[inlineIndex]
    const separatorToken = new state.Token('text', '', 0)
    separatorToken.content = ' '
    separatorToken.level = 0

    inlineToken.children = [
      ...createLeadingLinkTokens(),
      separatorToken,
      ...(inlineToken.children || [])
    ]
    inlineToken.content = `${text} ${inlineToken.content || ''}`
  })
}

// markdownPostCardPlugin 将已识别的顶层独占链接替换为文章卡片
const markdownPostCardPlugin = (markdown: MarkdownIt, postCards: EmbeddedPostCardMap) => {
  markdown.core.ruler.after('inline', 'post-card', state => {
    for (let index = 0; index < state.tokens.length - 2; index += 1) {
      const href = getStandalonePostTarget(state.tokens, index)
      const card = href ? postCards[href] : undefined
      if (!href || !card || card.status === 'failed') {
        continue
      }

      let content = ''
      if (card.status === 'loading') {
        content = `<span class="md-post-card md-post-card-skeleton" aria-hidden="true"><span class="md-post-card-main"><span class="md-post-card-skeleton-title md-post-card-skeleton-block"></span><span class="md-post-card-skeleton-summary md-post-card-skeleton-block"></span><span class="md-post-card-skeleton-summary is-short md-post-card-skeleton-block"></span><span class="md-post-card-skeleton-meta md-post-card-skeleton-block"></span></span></span>\n`
      } else if (card.content?.type === 'post') {
        const item = card.content
        const escapeHtml = markdown.utils.escapeHtml
        const title = escapeHtml(item.title || item.slug)
        const summary = escapeHtml(item.summary || '')
        const publishedAt = item.publishedAt || item.CreatedAt || ''
        const date = publishedAt ? formatDisplayDate(publishedAt) : ''
        const author = escapeHtml(item.authorName || '佚名')
        const dateHtml = date
          ? `<span class="md-post-card-meta-item"><i class="fa-regular fa-calendar" aria-hidden="true"></i><time datetime="${escapeHtml(publishedAt)}">${escapeHtml(date)}</time></span>`
          : ''
        const metaHtml = `<span class="md-post-card-meta">${dateHtml}<span class="md-post-card-meta-item"><i class="fa-regular fa-user" aria-hidden="true"></i>${author}</span><span class="md-post-card-meta-item"><i class="fa-regular fa-eye" aria-hidden="true"></i>${Number(item.viewCount || 0)}</span><span class="md-post-card-meta-item"><i class="fa-regular fa-heart" aria-hidden="true"></i>${Number(item.likeCount || 0)}</span><span class="md-post-card-meta-item"><i class="fa-regular fa-comment" aria-hidden="true"></i>${Number(item.commentCount || 0)}</span></span>`
        const cover = String(item.cover || '').trim()
        const coverHtml = cover
          ? `<img class="md-post-card-cover" src="${escapeHtml(resolveImageAssetUrl(cover))}" alt="" loading="lazy" decoding="async">`
          : ''
        content = `<a class="md-post-card${cover ? ' has-cover' : ''}" href="${escapeHtml(href)}"><span class="md-post-card-main"><strong class="md-post-card-title">${title}</strong><span class="md-post-card-summary">${summary}</span>${metaHtml}</span>${coverHtml}</a>\n`
      } else {
        continue
      }

      const token = new state.Token('html_block', '', 0)
      token.block = true
      token.content = content
      state.tokens.splice(index, 3, token)
    }
  })
}

const rendered = computed(() => {
  const toc: Array<{ id: string; text: string; level: number }> = []
  const slugCount = new Map<string, number>()
  const markdown = new MarkdownIt({
    html: true,
    breaks: true,
    linkify: true,
    typographer: true
  })
  markdown.use(markdownTaskListPlugin)
  markdown.use(markdownCalloutPlugin)
  markdown.use(markdownItFootnote)
  markdown.use(markdownItDeflist)
  markdown.use(markdownItAbbr)
  markdown.use(markdownItMark)
  markdown.use(markdownItSub)
  markdown.use(markdownItSup)
  markdown.use(markdownItEmoji)
  if (props.leadingLink) {
    markdown.use(markdownLeadingLinkPlugin, props.leadingLink)
  }
  if (props.postCards) {
    markdown.use(markdownPostCardPlugin, props.postCards)
  }
  markdown.use(texmath, {
    engine: katex,
    delimiters: 'dollars',
    katexOptions: {
      throwOnError: false,
      strict: 'ignore',
      trust: false
    }
  })

  const defaultImageRenderer =
    markdown.renderer.rules.image ||
    ((tokens, index, options, env, self) => self.renderToken(tokens, index, options))

  const defaultHeadingOpen =
    markdown.renderer.rules.heading_open ||
    ((tokens, index, options, env, self) => self.renderToken(tokens, index, options))
  const defaultFenceRenderer =
    markdown.renderer.rules.fence ||
    ((tokens, index, options, env, self) => self.renderToken(tokens, index, options))

  markdown.renderer.rules.image = (tokens, index, options, env, self) => {
    const token = tokens[index]
    const src = token.attrGet('src')
    if (src) {
      token.attrSet('src', resolveImageAssetUrl(src))
    }
    token.attrJoin('class', 'blog-progressive-img is-progressive')
    if (!token.attrGet('loading')) {
      token.attrSet('loading', 'lazy')
    }
    if (!token.attrGet('decoding')) {
      token.attrSet('decoding', 'async')
    }
    return defaultImageRenderer(tokens, index, options, env, self)
  }

  markdown.renderer.rules.heading_open = (tokens, index, options, env, self) => {
    const token = tokens[index]
    const level = Number(token.tag.slice(1))
    if (props.tocEnabled && !Number.isNaN(level) && level >= 1 && level <= 4) {
      const inline = tokens[index + 1]
      const rawText = inline?.type === 'inline' ? inline.content : ''
      const text = rawText.trim()
      if (text) {
        const base = slugifyContentHeading(text)
        const count = (slugCount.get(base) || 0) + 1
        slugCount.set(base, count)
        const id = count > 1 ? `${base}-${count}` : base
        token.attrSet('id', id)
        toc.push({ id, text, level })
      }
    }
    return defaultHeadingOpen(tokens, index, options, env, self)
  }

  markdown.renderer.rules.fence = (tokens, index, options, env, self) => {
    const token = tokens[index]
    if (!token) {
      return defaultFenceRenderer(tokens, index, options, env, self)
    }
    const escapeHtml = markdown.utils.escapeHtml
    const info = markdown.utils.unescapeAll(token.info || '').trim()
    const lang = (info.split(/\s+/, 1)[0] || '').toLowerCase()
    if (lang === 'mermaid') {
      return `<div class="md-mermaid"><div class="md-mermaid-output"></div><pre class="md-mermaid-source"><code class="language-mermaid">${escapeHtml(token.content || '')}</code></pre></div>`
    }
    const langClass = `${options.langPrefix || 'language-'}${lang || 'plaintext'}`
    let highlighted = escapeHtml(token.content || '')
    if (lang && hljs.getLanguage(lang)) {
      highlighted = hljs.highlight(token.content || '', {
        language: lang,
        ignoreIllegals: true
      }).value
    }
    const copyLabelEscaped = escapeHtml(copyLabel)
    const encodedCode = escapeHtml(encodeCopyPayload(token.content || ''))
    return `<div class="md-code-block"><div class="md-code-toolbar"><span class="md-code-lang">${escapeHtml(langLabel(lang))}</span><button class="md-code-copy" type="button" title="${copyLabelEscaped}" aria-label="${copyLabelEscaped}" data-code="${encodedCode}"><i class="md-code-copy-icon fa-regular fa-copy"></i><span class="md-code-copy-label">${copyLabelEscaped}</span></button></div><pre class="hljs"><code class="hljs ${escapeHtml(langClass)}">${highlighted}</code></pre></div>`
  }

  const source = props.decodeMarkdownMarkers ? decodeMarkdownBlockMarkers(props.source || '') : props.source || ''
  const renderedHtml = markdown.render(source)
  const safeHtml = sanitizeMarkdownHtml(renderedHtml)

  return {
    html: safeHtml,
    toc
  }
})

watch(
  () => rendered.value.toc,
  value => {
    emit('toc-change', value)
  },
  { immediate: true }
)

const html = computed(() => rendered.value.html)
const tocItemsEqual = (
  left: Array<{ id: string; text: string; level: number }>,
  right: Array<{ id: string; text: string; level: number }>
) => left.length === right.length && left.every((item, index) => {
  const target = right[index]
  return item.id === target?.id && item.text === target.text && item.level === target.level
})
let markdownMounted = false
let markdownRenderTaskID = 0
let mermaidRenderTaskID = 0
let mermaidModule: (typeof import('mermaid'))['default'] | null = null
let mermaidTheme: 'default' | 'dark' | null = null

const currentMermaidTheme = () => (colorMode.value === 'dark' ? 'dark' : 'default')

const loadMermaid = async () => {
  const theme = currentMermaidTheme()
  if (!mermaidModule) {
    const mod = await import('mermaid')
    mermaidModule = mod.default
  }
  if (mermaidTheme !== theme) {
    mermaidModule.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      theme,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    })
    mermaidTheme = theme
  }
  return mermaidModule
}

const applyCodeLineNumbers = async (taskID?: number) => {
  if (!import.meta.client || !markdownMounted) {
    return
  }
  const rootAtStart = markdownRootRef.value
  if (!rootAtStart || !rootAtStart.isConnected) {
    return
  }
  try {
    if (!lineNumbersReady.value) {
      ;(window as any).hljs = hljs
      await import('highlightjs-line-numbers.js')
      lineNumbersReady.value = true
    }
    if (taskID !== undefined && taskID !== markdownRenderTaskID) {
      return
    }
    const root = markdownRootRef.value
    if (!markdownMounted || !root || !root.isConnected) {
      return
    }
    const enhancer = (window as any).hljs?.lineNumbersBlockSync
    if (typeof enhancer !== 'function') {
      return
    }
    const blocks = root.querySelectorAll('pre.hljs > code.hljs')
    blocks.forEach(node => {
      const code = node as HTMLElement
      if (!code?.isConnected || !code.parentElement) {
        return
      }
      if (code.querySelector('table.hljs-ln')) {
        return
      }
      try {
        enhancer(code, { singleLine: true })
      } catch {
        // 快速切页时忽略插件内部对已卸载节点的访问异常。
      }
    })
  } catch {
    // 竞态场景下避免向外抛出未捕获异常。
  }
}

const applyMermaidDiagrams = async (taskID?: number) => {
  if (!import.meta.client || !markdownMounted) {
    return
  }
  const rootAtStart = markdownRootRef.value
  if (!rootAtStart || !rootAtStart.isConnected) {
    return
  }
  const diagrams = Array.from(rootAtStart.querySelectorAll('.md-mermaid')) as HTMLElement[]
  if (!diagrams.length) {
    return
  }

  try {
    const mermaid = await loadMermaid()
    if (taskID !== undefined && taskID !== mermaidRenderTaskID) {
      return
    }

    for (const [index, block] of diagrams.entries()) {
      if (!markdownMounted || !block.isConnected || (taskID !== undefined && taskID !== mermaidRenderTaskID)) {
        return
      }
      const output = block.querySelector('.md-mermaid-output') as HTMLElement | null
      const code = block.querySelector('.md-mermaid-source code') as HTMLElement | null
      const source = code?.textContent || ''
      if (!output || !source.trim()) {
        continue
      }

      block.classList.remove('is-rendered', 'has-error')
      block.classList.add('is-rendering')
      output.textContent = ''

      try {
        const id = `md-mermaid-${taskID ?? 0}-${index}`
        const { svg, bindFunctions } = await mermaid.render(id, source, output)
        if (!markdownMounted || !block.isConnected || (taskID !== undefined && taskID !== mermaidRenderTaskID)) {
          return
        }
        output.innerHTML = svg
        bindFunctions?.(output)
        block.classList.add('is-rendered')
      } catch {
        output.textContent = mermaidRenderErrorText
        block.classList.add('has-error')
      } finally {
        block.classList.remove('is-rendering')
      }
    }
  } catch {
    diagrams.forEach(block => {
      const output = block.querySelector('.md-mermaid-output') as HTMLElement | null
      if (!output) {
        return
      }
      output.textContent = mermaidRenderErrorText
      block.classList.remove('is-rendering')
      block.classList.add('has-error')
    })
  }
}

const onMarkdownClick = async (event: MouseEvent) => {
  const target = event.target as HTMLElement | null
  const button = target?.closest('.md-code-copy') as HTMLButtonElement | null
  if (button) {
    const labelNode = button.querySelector('.md-code-copy-label') as HTMLElement | null
    const setCopyLabel = (label: string) => {
      if (labelNode) {
        labelNode.textContent = label
        return
      }
      button.textContent = label
    }
    event.preventDefault()
    const encoded = button.getAttribute('data-code') || ''
    const code = button.closest('.md-code-block')?.querySelector('code')
    const text = decodeCopyPayload(encoded) || code?.textContent || ''
    if (!text || !navigator?.clipboard?.writeText) {
      setCopyLabel(copyFailedLabel)
      window.setTimeout(() => {
        setCopyLabel(copyLabel)
      }, 1200)
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      setCopyLabel(copiedLabel)
    } catch {
      setCopyLabel(copyFailedLabel)
    }
    window.setTimeout(() => {
      setCopyLabel(copyLabel)
    }, 1200)
    return
  }

  const postCard = target?.closest('a.md-post-card') as HTMLAnchorElement | null
  if (postCard) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      postCard.target === '_blank'
    ) {
      return
    }
    const targetURL = new URL(postCard.href, window.location.href)
    if (targetURL.origin === window.location.origin) {
      event.preventDefault()
      await navigateTo(`${targetURL.pathname}${targetURL.search}${targetURL.hash}`)
    }
    return
  }

  const image = target?.closest('img') as HTMLImageElement | null
  if (!image || !markdownRootRef.value) {
    return
  }

  const imageNodes = (Array.from(markdownRootRef.value.querySelectorAll('img')) as HTMLImageElement[]).filter(node => {
    const src = node.getAttribute('src')
    return typeof src === 'string' && src.trim().length > 0
  })
  const sourceIndex = imageNodes.indexOf(image)
  if (sourceIndex < 0) {
    return
  }
  const imageSources = imageNodes
    .map((node): LightboxSource | null => {
      const src = (node.getAttribute('src') || '').trim()
      if (!src) {
        return null
      }
      const title = (node.getAttribute('title') || '').trim()
      const alt = (node.getAttribute('alt') || '').trim()
      return {
        src,
        title: title || undefined,
        alt: alt || undefined
      }
    })
    .filter((image): image is LightboxSource => image !== null)
  event.preventDefault()
  openMarkdownLightboxImages(imageSources, sourceIndex)
}

let releaseProgressiveImages: (() => void) | null = null
const scheduleMarkdownEnhancement = () => {
  const taskID = ++markdownRenderTaskID
  nextTick(() => {
    if (!markdownMounted || taskID !== markdownRenderTaskID) {
      return
    }
    void applyCodeLineNumbers(taskID)
    void applyMermaidDiagrams(++mermaidRenderTaskID)
    if (!markdownMounted || taskID !== markdownRenderTaskID) {
      return
    }
    const domToc = props.tocEnabled ? collectContentTocItems(markdownRootRef.value) : []
    if (!tocItemsEqual(domToc, rendered.value.toc)) {
      emit('toc-change', domToc)
    }
    releaseProgressiveImages?.()
    releaseProgressiveImages = bindProgressiveImages(markdownRootRef.value)
  })
}

onMounted(() => {
  markdownMounted = true
  markdownRootRef.value?.addEventListener('click', onMarkdownClick)
  scheduleMarkdownEnhancement()
})

onBeforeUnmount(() => {
  markdownMounted = false
  markdownRenderTaskID += 1
  mermaidRenderTaskID += 1
  markdownRootRef.value?.removeEventListener('click', onMarkdownClick)
  releaseProgressiveImages?.()
  releaseProgressiveImages = null
})

watch(
  () => html.value,
  () => {
    if (!markdownMounted) {
      return
    }
    scheduleMarkdownEnhancement()
  }
)

watch(
  () => colorMode.value,
  () => {
    if (!markdownMounted) {
      return
    }
    void applyMermaidDiagrams(++mermaidRenderTaskID)
  }
)
</script>

<template>
  <article ref="markdownRootRef" class="md-view" v-html="html" />
  <BlogEasyLightbox
    :visible="markdownLightboxVisible"
    :sources="markdownLightboxSources"
    :index="markdownLightboxIndex"
    @hide="closeMarkdownLightbox"
  />
</template>

<style scoped>
.md-view {
  color: var(--text);
  font-size: 16px;
  line-height: 1.82;
  word-break: break-word;
}

.md-view :deep(h1),
.md-view :deep(h2),
.md-view :deep(h3),
.md-view :deep(h4) {
  margin-top: 1.45em;
  margin-bottom: 0.7em;
  line-height: 1.35;
  scroll-margin-top: var(--anchor-offset);
}

.md-view :deep(ul),
.md-view :deep(ol) {
  padding-left: 1.5em;
}

.md-view :deep(ul) {
  list-style-type: disc;
}

.md-view :deep(ul ul) {
  list-style-type: circle;
}

.md-view :deep(ul ul ul) {
  list-style-type: square;
}

.md-view :deep(li + li) {
  margin-top: 0.35em;
}

.md-view :deep(.task-list) {
  padding-left: 0;
  list-style: none;
}

.md-view :deep(.task-list-item) {
  display: flex;
  align-items: center;
  gap: 8px;
}

.md-view :deep(.task-list-item + .task-list-item) {
  margin-top: 0.42em;
}

.md-view :deep(.task-list-item > p) {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.md-view :deep(.task-list-checkbox) {
  width: 15px;
  height: 15px;
  margin: 0;
  accent-color: var(--brand);
  pointer-events: none;
  flex-shrink: 0;
}

.md-view :deep(hr) {
  margin: 1.6em 0;
  border: 0;
  border-top: 1px solid var(--line);
}

.md-view :deep(a) {
  color: var(--brand);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.md-view :deep(.md-post-card) {
  position: relative;
  display: block;
  margin: 1.2em 0;
  overflow: hidden;
  border-radius: var(--radius);
  background: var(--bg);
  color: var(--text);
  text-decoration: none;
}

.md-view :deep(a.md-post-card:hover .md-post-card-main) {
  background: color-mix(in srgb, var(--brand-soft) 45%, var(--bg) 55%);
}

.md-view :deep(.md-post-card-main) {
  display: block;
  padding: 14px 16px;
  transition: background-color 0.18s ease;
}

.md-view :deep(.md-post-card.has-cover .md-post-card-main) {
  padding-right: 210px;
}

.md-view :deep(.md-post-card-title) {
  display: -webkit-box;
  overflow: hidden;
  margin-bottom: 7px;
  font-size: 18px;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.md-view :deep(.md-post-card-summary) {
  display: -webkit-box;
  overflow: hidden;
  color: var(--text-soft);
  font-size: 14px;
  line-height: 1.65;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.md-view :deep(.md-post-card-meta) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 14px;
  margin-top: 6px;
  color: var(--text-soft);
  font-size: 12px;
  line-height: 1.5;
}

.md-view :deep(.md-post-card-meta-item) {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.md-view :deep(.md-post-card-meta-item i) {
  color: color-mix(in srgb, var(--brand) 68%, var(--text-soft) 32%);
  font-size: 11px;
}

.md-view :deep(.md-post-card-cover) {
  position: absolute;
  top: 0;
  right: 0;
  width: 210px;
  height: 100%;
  object-fit: cover;
  -webkit-mask-image: linear-gradient(to right, transparent 0, rgba(0, 0, 0, 0.18) 18px, rgba(0, 0, 0, 0.7) 42px, #000 60px);
  mask-image: linear-gradient(to right, transparent 0, rgba(0, 0, 0, 0.18) 18px, rgba(0, 0, 0, 0.7) 42px, #000 60px);
}

.md-view :deep(.md-post-card-skeleton) {
  pointer-events: none;
}

.md-view :deep(.md-post-card-skeleton-block) {
  position: relative;
  display: block;
  overflow: hidden;
  border-radius: 4px;
  background: var(--album-skeleton-background);
}

.md-view :deep(.md-post-card-skeleton-block::after) {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: var(--album-skeleton-shimmer);
  background-size: var(--album-skeleton-shimmer-size) 100%;
  animation: md-post-card-shimmer var(--album-skeleton-duration) linear infinite;
}

.md-view :deep(.md-post-card-skeleton-title) {
  width: 68%;
  height: 21px;
  margin-bottom: 12px;
}

.md-view :deep(.md-post-card-skeleton-summary) {
  width: 100%;
  height: 13px;
  margin-bottom: 8px;
}

.md-view :deep(.md-post-card-skeleton-summary.is-short) {
  width: 72%;
  margin-bottom: 0;
}

.md-view :deep(.md-post-card-skeleton-meta) {
  width: 42%;
  height: 12px;
  margin-top: 8px;
}

@keyframes md-post-card-shimmer {
  100% {
    transform: translateX(100%);
  }
}

.md-view :deep(table:not(.hljs-ln)) {
  width: 100%;
  margin: 1.2em 0;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid var(--line);
  border-radius: 6px;
  overflow: hidden;
  background: var(--bg);
}

.md-view :deep(table:not(.hljs-ln) th),
.md-view :deep(table:not(.hljs-ln) td) {
  padding: 10px 12px;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  text-align: left;
  vertical-align: top;
}

.md-view :deep(table:not(.hljs-ln) th:last-child),
.md-view :deep(table:not(.hljs-ln) td:last-child) {
  border-right: 0;
}

.md-view :deep(table:not(.hljs-ln) tr:last-child td) {
  border-bottom: 0;
}

.md-view :deep(table:not(.hljs-ln) th) {
  background: var(--bg-soft);
  font-weight: 600;
}

.md-view :deep(table:not(.hljs-ln) tbody tr:nth-child(even)) {
  background: color-mix(in srgb, var(--bg-soft) 65%, var(--bg) 35%);
}

.md-view :deep(table:not(.hljs-ln) tbody tr:hover) {
  background: color-mix(in srgb, var(--brand-soft) 40%, var(--bg) 60%);
}

.md-view :deep(pre:not(.hljs)) {
  overflow: auto;
  margin: 1.2em 0;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--bg-soft);
}

.md-view :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92em;
}

.md-view :deep(:not(pre) > code) {
  padding: 0.16em 0.42em;
  border-radius: 4px;
  border: 1px solid var(--line);
  background: var(--bg-soft);
  color: var(--text);
}

.md-view :deep(pre code) {
  display: block;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
  tab-size: 4;
  -moz-tab-size: 4;
}

.md-view :deep(.md-code-block),
.md-view :deep(.md-mermaid) {
  margin: 1.2em 0;
  overflow: hidden;
  border-radius: var(--radius);
}

.md-view :deep(.md-code-block) {
  --md-code-content-bg: color-mix(in srgb, var(--bg) 70%, var(--bg-soft) 30%);
  border: 0;
  background: var(--md-code-content-bg);
}

.md-view :deep(.md-mermaid) {
  border: 1px solid var(--line);
  background: var(--bg-soft);
}

.md-view :deep(.md-code-toolbar) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 10px;
  background: var(--bg);
}

.md-view :deep(.md-code-lang) {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--text-soft);
}

.md-view :deep(.md-code-copy) {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  border-radius: 999px;
  padding: 5px 12px;
  background: transparent;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: color 0.18s ease;
}

.md-view :deep(.md-code-copy-icon) {
  font-size: 12px;
  line-height: 1;
}

.md-view :deep(.md-code-copy:hover) {
  color: var(--brand);
}

.md-view :deep(.md-code-block > pre.hljs) {
  overflow: auto;
  margin: 0;
  padding: 14px 16px;
  border: 0;
  border-radius: 0;
  background: var(--md-code-content-bg);
}

.md-view :deep(.md-code-block > pre.hljs > code.hljs) {
  background: transparent;
  padding: 0;
}

.md-view :deep(.hljs-ln) {
  width: 100%;
  border-collapse: collapse;
}

.md-view :deep(.hljs-ln td) {
  padding: 0;
  border: 0;
  vertical-align: top;
}

.md-view :deep(.hljs-ln-numbers) {
  width: 3.1em;
  min-width: 3.1em;
  margin-right: 10px!important;
  color: var(--text-soft);
  opacity: 0.72;

  user-select: none;
}

.md-view :deep(.hljs-ln-numbers .hljs-ln-n) {
  margin-right: 10px;
}

.md-view :deep(.hljs-ln-code) {
  padding-left: 12px;
  tab-size: 4;
  -moz-tab-size: 4;
}

.md-view :deep(.md-mermaid-output) {
  display: flex;
  min-height: 96px;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
  padding: 18px;
  color: var(--text-soft);
}

.md-view :deep(.md-mermaid-output svg) {
  max-width: 100%;
  height: auto;
}

.md-view :deep(.md-mermaid.is-rendered .md-mermaid-source) {
  display: none;
}

.md-view :deep(.md-mermaid.has-error .md-mermaid-output) {
  min-height: auto;
  justify-content: flex-start;
  border-bottom: 1px solid var(--line);
  color: var(--accent);
  font-size: 13px;
  font-weight: 600;
}

.md-view :deep(.md-mermaid .md-mermaid-source) {
  margin: 0;
  border-width: 1px 0 0;
  border-radius: 0;
}

.md-view :deep(img) {
  max-width: 100%;
  border-radius: 10px;
  cursor: pointer;
}

.md-view :deep(blockquote) {
  width: 100%;
  margin-left: 0;
  padding: 10px 14px;
  border-left: 4px solid var(--brand);
  background: var(--brand-soft);
}

.md-view :deep(.callout) {
  --callout-color: #0969da;
  --callout-background-color: rgba(9, 105, 218, 0.08);
  margin: 0 0 16px;
  padding: 0.75em 1em;
  border-left: 0.25em solid var(--callout-color);
  background: var(--callout-background-color);
}

.md-view :deep(.callout[data-subtype='TIP']) {
  --callout-color: #1a7f37;
  --callout-background-color: rgba(26, 127, 55, 0.08);
}

.md-view :deep(.callout[data-subtype='IMPORTANT']) {
  --callout-color: #8250df;
  --callout-background-color: rgba(130, 80, 223, 0.08);
}

.md-view :deep(.callout[data-subtype='WARNING']) {
  --callout-color: #9a6700;
  --callout-background-color: rgba(154, 103, 0, 0.08);
}

.md-view :deep(.callout[data-subtype='CAUTION']) {
  --callout-color: #cf222e;
  --callout-background-color: rgba(207, 34, 46, 0.08);
}

.md-view :deep(.callout-info) {
  display: flex;
  align-items: center;
  min-height: 1.5em;
  margin-bottom: 0.5em;
  color: var(--callout-color);
  font-weight: 600;
}

.md-view :deep(.callout-content > :last-child) {
  margin-bottom: 0;
}

.md-view :deep(mark) {
  padding: 0.08em 0.34em;
  border-radius: 4px;
  background: color-mix(in srgb, var(--brand-soft) 64%, #fff 36%);
  color: var(--text);
}

.md-view :deep(abbr[title]) {
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: help;
}

.md-view :deep(dl) {
  margin: 1em 0;
}

.md-view :deep(dt) {
  font-weight: 600;
  margin-top: 0.72em;
}

.md-view :deep(dd) {
  margin: 0.35em 0 0.75em 1.1em;
  color: var(--text-soft);
}

.md-view :deep(.footnote-ref) {
  margin: 0 0.06em;
  font-size: 0.85em;
  vertical-align: super;
}

.md-view :deep(.footnotes) {
  margin-top: 1.65em;
  padding-top: 0.95em;
  border-top: 1px solid var(--line);
  color: var(--text-soft);
  font-size: 0.94em;
}

.md-view :deep(.footnotes > .footnotes-list) {
  margin: 0.72em 0 0;
  padding-left: 1.35em;
}

.md-view :deep(.footnotes .footnote-item) {
  margin: 0.38em 0;
}

.md-view :deep(.footnotes .footnote-item p) {
  margin: 0;
}

.md-view :deep(.footnotes .footnote-backref) {
  margin-left: 0.35em;
  text-decoration: none;
}

.md-view :deep(details) {
  margin: 0.72em 0;
  padding: 0.2em 0 0.2em 0.2em;
}

.md-view :deep(details > summary) {
  cursor: pointer;
  font-weight: 600;
}

.md-view :deep(details > summary + *) {
  margin-top: 0.5em;
}

.md-view :deep(details details) {
  margin-top: 0.55em;
  margin-left: 1.05em;
  padding-left: 0.85em;
  border-left: 1px dashed color-mix(in srgb, var(--line) 78%, var(--brand) 22%);
}

.md-view :deep(.katex) {
  font-size: 1.03em;
}

.md-view :deep(.katex-display) {
  margin: 1.15em 0;
  padding: 0.35em 0;
  overflow-x: auto;
  overflow-y: hidden;
}

html[data-theme='dark'] .md-view :deep(.hljs) {
  color: var(--code-text);
  background: var(--code-background);
}

html[data-theme='dark'] .md-view :deep(.md-code-block > pre.hljs) {
  background: var(--md-code-content-bg);
}

html[data-theme='dark'] .md-view :deep(.callout) {
  --callout-color: #58a6ff;
  --callout-background-color: rgba(56, 139, 253, 0.15);
}

html[data-theme='dark'] .md-view :deep(.callout[data-subtype='TIP']) {
  --callout-color: #3fb950;
  --callout-background-color: rgba(46, 160, 67, 0.15);
}

html[data-theme='dark'] .md-view :deep(.callout[data-subtype='IMPORTANT']) {
  --callout-color: #a371f7;
  --callout-background-color: rgba(163, 113, 247, 0.15);
}

html[data-theme='dark'] .md-view :deep(.callout[data-subtype='WARNING']) {
  --callout-color: #d29922;
  --callout-background-color: rgba(187, 128, 9, 0.15);
}

html[data-theme='dark'] .md-view :deep(.callout[data-subtype='CAUTION']) {
  --callout-color: #f85149;
  --callout-background-color: rgba(248, 81, 73, 0.15);
}

html[data-theme='dark'] .md-view :deep(.hljs-doctag),
html[data-theme='dark'] .md-view :deep(.hljs-keyword),
html[data-theme='dark'] .md-view :deep(.hljs-meta .hljs-keyword),
html[data-theme='dark'] .md-view :deep(.hljs-template-tag),
html[data-theme='dark'] .md-view :deep(.hljs-template-variable),
html[data-theme='dark'] .md-view :deep(.hljs-type),
html[data-theme='dark'] .md-view :deep(.hljs-variable.language_) {
  color: #ff7b72;
}

html[data-theme='dark'] .md-view :deep(.hljs-title),
html[data-theme='dark'] .md-view :deep(.hljs-title.class_),
html[data-theme='dark'] .md-view :deep(.hljs-title.class_.inherited__),
html[data-theme='dark'] .md-view :deep(.hljs-title.function_) {
  color: #d2a8ff;
}

html[data-theme='dark'] .md-view :deep(.hljs-attr),
html[data-theme='dark'] .md-view :deep(.hljs-attribute),
html[data-theme='dark'] .md-view :deep(.hljs-literal),
html[data-theme='dark'] .md-view :deep(.hljs-meta),
html[data-theme='dark'] .md-view :deep(.hljs-number),
html[data-theme='dark'] .md-view :deep(.hljs-operator),
html[data-theme='dark'] .md-view :deep(.hljs-selector-attr),
html[data-theme='dark'] .md-view :deep(.hljs-selector-class),
html[data-theme='dark'] .md-view :deep(.hljs-selector-id),
html[data-theme='dark'] .md-view :deep(.hljs-variable) {
  color: #79c0ff;
}

html[data-theme='dark'] .md-view :deep(.hljs-meta .hljs-string),
html[data-theme='dark'] .md-view :deep(.hljs-regexp),
html[data-theme='dark'] .md-view :deep(.hljs-string) {
  color: #a5d6ff;
}

html[data-theme='dark'] .md-view :deep(.hljs-built_in),
html[data-theme='dark'] .md-view :deep(.hljs-symbol) {
  color: #ffa657;
}

html[data-theme='dark'] .md-view :deep(.hljs-code),
html[data-theme='dark'] .md-view :deep(.hljs-comment),
html[data-theme='dark'] .md-view :deep(.hljs-formula) {
  color: #8b949e;
}

html[data-theme='dark'] .md-view :deep(.hljs-name),
html[data-theme='dark'] .md-view :deep(.hljs-quote),
html[data-theme='dark'] .md-view :deep(.hljs-selector-pseudo),
html[data-theme='dark'] .md-view :deep(.hljs-selector-tag) {
  color: #7ee787;
}

html[data-theme='dark'] .md-view :deep(.hljs-subst) {
  color: var(--code-text);
}

html[data-theme='dark'] .md-view :deep(.hljs-section) {
  color: var(--brand);
  font-weight: 700;
}

html[data-theme='dark'] .md-view :deep(.hljs-bullet) {
  color: #f2cc60;
}

html[data-theme='dark'] .md-view :deep(.hljs-emphasis) {
  color: var(--code-text);
  font-style: italic;
}

html[data-theme='dark'] .md-view :deep(.hljs-strong) {
  color: var(--code-text);
  font-weight: 700;
}

html[data-theme='dark'] .md-view :deep(.hljs-addition) {
  color: #aff5b4;
  background-color: #033a16;
}

html[data-theme='dark'] .md-view :deep(.hljs-deletion) {
  color: #ffdcd7;
  background-color: #67060c;
}

@media (max-width: 640px) {
  .md-view :deep(.md-post-card-main) {
    padding: 12px;
  }

  .md-view :deep(.md-post-card.has-cover .md-post-card-main) {
    padding-right: 120px;
  }

  .md-view :deep(.md-post-card-title) {
    font-size: 16px;
    -webkit-line-clamp: 1;
  }

  .md-view :deep(.md-post-card-summary) {
    font-size: 13px;
    line-height: 1.6;
  }

  .md-view :deep(.md-post-card-cover) {
    width: 120px;
    -webkit-mask-image: linear-gradient(to right, transparent 0, rgba(0, 0, 0, 0.22) 12px, rgba(0, 0, 0, 0.72) 28px, #000 42px);
    mask-image: linear-gradient(to right, transparent 0, rgba(0, 0, 0, 0.22) 12px, rgba(0, 0, 0, 0.72) 28px, #000 42px);
  }

  .md-view :deep(.md-post-card-meta) {
    gap: 3px 10px;
    margin-top: 4px;
    font-size: 11px;
  }

  .md-view :deep(.md-post-card-skeleton-meta) {
    width: 52%;
    margin-top: 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .md-view :deep(.md-post-card-main) {
    transition: none;
  }

  .md-view :deep(.md-post-card-skeleton-block::after) {
    animation: none;
  }
}
</style>
